import { useMemo, useState } from "react";
import { BatchMaterialsCheck } from "@/components/planning/BatchMaterialsCheck";
import { RestockInfo } from "@/components/production/RunBadge";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { productMatches, searchWords } from "@/lib/search";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Search, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CareSymbolPicker } from "@/components/CareSymbols";
import { codeIssue } from "@/lib/modelSheets";
import { CURRENT_SEASON } from "@/lib/season";

const baseCode = (sku) => (sku || "").split("-").slice(0, 3).join("-").toUpperCase();
const SheetBadge = () => <span className="ml-2 rounded border border-primary/40 px-1 text-[10px] text-primary">Scheda modello · non ancora su Shopify</span>;

export const ProductPicker = ({ products, selected, onSelect }) => {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const words = searchWords(q);
    if (!words.length) return products.slice(0, 40);
    const code = words.join("-");
    const list = products.filter((p) => productMatches(p, words));
    // Prima i prodotti il cui codice inizia esattamente con quanto scritto
    list.sort((a, b) => (!(b.sku || "").toLowerCase().startsWith(code)) - (!(a.sku || "").toLowerCase().startsWith(code)));
    return list.slice(0, 40);
  }, [products, q]);

  if (selected) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-border p-2" data-testid="batch-selected-product">
        {selected.image_url && <img src={selected.image_url} alt="" className="h-12 w-10 rounded object-cover" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{selected.name}{selected.from_sheet && <SheetBadge />}</p>
          <p className="text-xs text-muted-foreground">{[baseCode(selected.sku), selected.color, selected.category, selected.season].filter(Boolean).join(" · ")}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onSelect(null)} data-testid="batch-clear-product"><X className="h-4 w-4" /></Button>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border">
      <div className="relative border-b border-border">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input autoFocus placeholder="Cerca per codice (es. 8001 a26 nero), articolo o colore..." value={q} onChange={(e) => setQ(e.target.value)}
          className="border-0 pl-9 focus-visible:ring-0" data-testid="batch-product-search" />
      </div>
      <div className="max-h-56 overflow-y-auto">
        {results.map((p) => (
          <button key={p.id} type="button" onClick={() => onSelect(p)} data-testid={`batch-product-option-${p.id}`}
            className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent">
            {p.image_url ? <img src={p.image_url} alt="" className="h-9 w-7 rounded object-cover" /> : <div className="h-9 w-7 rounded bg-muted" />}
            <div className="min-w-0">
              <p className="truncate text-sm">{p.name}{p.from_sheet && <SheetBadge />}</p>
              <p className="text-xs text-muted-foreground">{p.sku && <span className="mr-1 font-mono-erp text-foreground/80">{baseCode(p.sku)}</span>}{[p.color, p.season].filter(Boolean).join(" · ")}</p>
            </div>
          </button>
        ))}
        {results.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Nessun prodotto trovato</p>}
      </div>
    </div>
  );
};

export const SizeGrid = ({ sizes, values, onChange, testid = "batch-size" }) => (
  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
    {sizes.map((s) => (
      <div key={s} className="space-y-1">
        <Label className="text-xs text-muted-foreground">{s}</Label>
        <Input type="number" min="0" value={values[s] ?? ""} onChange={(e) => onChange({ ...values, [s]: e.target.value })}
          data-testid={`${testid}-${s}`} />
      </div>
    ))}
  </div>
);

export const LabFields = ({ lab, date, onLab, onDate, labs, testid = "batch" }) => (
  <>
    <div className="space-y-1.5">
      <Label>Laboratorio esterno</Label>
      <Select value={lab || undefined} onValueChange={onLab}>
        <SelectTrigger data-testid={`${testid}-lab`}><SelectValue placeholder="Scegli il laboratorio" /></SelectTrigger>
        <SelectContent>{[...labs, ...(lab && !labs.includes(lab) ? [lab] : [])].map((l) => <SelectItem key={l} value={l} data-testid={`${testid}-lab-${l}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label>Consegna prevista</Label>
      <Input type="date" value={date} onChange={(e) => onDate(e.target.value)} data-testid={`${testid}-delivery`} />
    </div>
  </>
);

const BLANK = { phase: "programmato", notes: "", lab: "", expected_delivery: "" };
const seasonRank = (s) => (s === CURRENT_SEASON ? 0 : 1);

export const NewBatchDialog = ({ phases }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState({});
  const [form, setForm] = useState(BLANK);
  const [care, setCare] = useState({});
  const { data: products = [] } = useQuery({
    queryKey: ["products"], enabled: open,
    queryFn: async () => (await api.get("/products")).data,
  });
  const { data: sheetOpts = [] } = useQuery({ queryKey: ["production-sheet-options"], enabled: open, queryFn: async () => (await api.get("/production/sheet-options")).data });
  const { data: labs = [] } = useQuery({ queryKey: ["labs"], enabled: open, queryFn: async () => (await api.get("/production/labs")).data });
  const sorted = useMemo(() => [...products, ...sheetOpts].sort((a, b) => seasonRank(a.season) - seasonRank(b.season) || (a.name || "").localeCompare(b.name || "")), [products, sheetOpts]);
  const total = Object.values(qty).reduce((s, v) => s + (Number(v) || 0), 0);

  const reset = () => { setProduct(null); setQty({}); setForm(BLANK); setCare({}); };
  const create = useMutation({
    mutationFn: async (p) => (await api.post("/production", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["production"] }); qc.invalidateQueries({ queryKey: ["production-seasons"] }); qc.invalidateQueries({ queryKey: ["labs"] }); qc.invalidateQueries({ queryKey: ["products"] }); toast.success("Lotto di produzione creato"); setOpen(false); reset(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const save = () => {
    if (!product) { toast.error("Seleziona un prodotto"); return; }
    if (!total) { toast.error("Inserisci i pezzi da produrre per almeno una taglia"); return; }
    create.mutate({
      ...(product.from_sheet ? { sheet_id: product.sheet_id, color: product.color || null } : { product_id: product.id }),
      phase: form.phase, notes: form.notes || null,
      lab: form.lab || null, expected_delivery: form.expected_delivery || null,
      care_symbols: Object.fromEntries(Object.entries(care).filter(([, v]) => v)),
      sizes: Object.entries(qty).map(([size, v]) => ({ size, quantity: Number(v) || 0 })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button data-testid="new-batch-btn"><Plus className="mr-2 h-4 w-4" />Nuovo lotto</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto [&>*]:min-w-0">
        <DialogHeader><DialogTitle>Nuovo lotto di produzione</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Prodotto</Label>
            <ProductPicker products={sorted} selected={product} onSelect={(p) => { setProduct(p); setQty({}); setCare(p?.care_symbols || {}); }} />
            {product?.from_sheet && codeIssue(product.sku, product.season) && (
              <div className="flex gap-1.5 rounded bg-amber-500/10 px-2 py-1.5 text-xs text-amber-400" data-testid="batch-sheet-code-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{codeIssue(product.sku, product.season)} Puoi creare il lotto lo stesso, ma correggi il codice nella scheda in Modellistica.</span>
              </div>
            )}
          </div>
          {product && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Pezzi per taglia</Label>
                <span className="text-xs text-muted-foreground" data-testid="batch-total">Totale: {num(total)} pz</span>
              </div>
              <SizeGrid sizes={(product.sizes || []).map((s) => s.size)} values={qty} onChange={setQty} />
            </div>
          )}
          <RestockInfo product={product} />
          <BatchMaterialsCheck product={product} quantity={total} />
          {product && (
            <div className="space-y-1.5 rounded-md border border-border/60 p-3">
              <Label>Simboli di lavaggio</Label>
              <p className="text-[11px] text-muted-foreground">{product.from_sheet ? "Presi dalla scheda tecnica del modello." : "Salvati su tutti i colori di questo articolo."}</p>
              <CareSymbolPicker value={care} onChange={setCare} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <p className="col-span-2 text-[11px] text-muted-foreground">Il lotto parte in Programmato: il tessuto si scala quando passa in Taglio, gli accessori in Confezione.</p>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="batch-notes" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={create.isPending} data-testid="save-batch-btn">
            {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea lotto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
